import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight"
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined"
import Avatar from "@mui/material/Avatar"
import Collapse from "@mui/material/Collapse"
import Divider from "@mui/material/Divider"
import Link from "@mui/material/Link"
import Typography from "@mui/material/Typography"
import { alpha, styled } from "@mui/material/styles"
import { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom"

import Column from "../../../common/components/Column"
import Row from "../../../common/components/Row"
import NavLogo from "../../../static/images/sidebar-icons/nav-logo.png"
import {
  ADMIN_NAV_FOOTER,
  ADMIN_NAV_GROUPS,
  DASHBOARD_NAV,
  defaultOpenGroups,
  findActiveNavGroupId,
  isNavEntryActive,
  isNavGroupActive,
  navGroupsForFeatures,
  readStoredOpenGroups,
  writeStoredOpenGroups,
} from "../adminNavConfig"
import { adminLogout, selectAdminProfile } from "../reducer"

const ADMIN_SIDEBAR_WIDTH = "268px"

const shouldForwardProp = {
  shouldForwardProp: (prop) =>
    !["extended", "isActive", "isChild"].includes(prop),
}

const LinksColumn = styled(Column)(({ theme }) => ({
  paddingInline: "14px",
  paddingBlock: theme.spacing(3),
  alignItems: "flex-start",
  position: "sticky",
  top: 0,
  left: 0,
  width: ADMIN_SIDEBAR_WIDTH,
  height: "100vh",
  minHeight: "680px",
  flexShrink: 0,
  background:
    "linear-gradient(159deg, rgba(137, 47, 206, 1) 3%, rgba(137, 47, 206, 0.8) 100%)",
  backdropFilter: "blur(12px)",
  zIndex: theme.zIndex.drawer,
}))

const LinkGroupColumn = styled(Column)(() => ({
  gap: "2px",
  width: "100%",
}))

const CommonLink = styled(
  Row,
  shouldForwardProp
)(({ theme, isActive, isChild }) => ({
  justifyContent: "flex-start",
  alignItems: "center",
  padding: theme.spacing(1.5),
  paddingLeft: isChild ? theme.spacing(2.5) : theme.spacing(1.5),
  gap: theme.spacing(1.5),
  width: "100%",
  minHeight: "44px",
  backgroundColor: isActive
    ? alpha(theme.palette.secondary.light, 0.9)
    : "inherit",
  boxShadow: isActive ? "rgba(0, 0, 0, 0.24) 0px 3px 8px" : "none",
  backdropFilter: isActive ? "blur(21px)" : "none",
  WebkitBackdropFilter: isActive ? "blur(21px)" : "none",
  textDecoration: "none",
  border: isActive ? "1px solid rgba(255, 255, 255, 0.25)" : "none",
  cursor: "pointer",
  color: "white",
  "&:hover": {
    backgroundColor: isActive
      ? theme.palette.secondary.light
      : alpha(theme.palette.secondary.light, 0.6),
  },
}))

const LinkContainer = styled(CommonLink)(() => ({
  borderRadius: "24px",
}))

const AvatarLink = styled(CommonLink)(({ theme }) => ({
  background:
    "linear-gradient(159deg, rgba(137, 47, 206, 1) 3%, rgba(137, 47, 206, 0.8) 100%)",
  backdropFilter: "blur(21px)",
  border: "1px solid rgba(255, 255, 255, 0.15)",
  boxShadow: "0 28px 90px 0 rgba(0, 0, 0, 0.7)",
  borderRadius: "32px",
  height: "auto",
  minHeight: "50px",
  paddingBlock: theme.spacing(1.25),
}))

const LinkTypography = styled(Typography)(() => ({
  color: "white",
  textShadow: "1px 2px 10px rgba(0, 0, 0, 0.2)",
  textWrap: "nowrap",
  fontFamily: "Inter, sans-serif",
  fontSize: "14px",
  fontWeight: 600,
  lineHeight: 1.2,
}))

const SubtleLinkTypography = styled(Typography)(() => ({
  color: "rgba(255, 255, 255, 0.78)",
  fontFamily: "Inter, sans-serif",
  fontSize: "12px",
  lineHeight: 1.2,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}))

const brandWordSx = {
  fontFamily: "Montserrat, sans-serif",
  fontWeight: 700,
  color: "white",
  lineHeight: 1.1,
}

function AdminNavLink({ item, isChild = false }) {
  const location = useLocation()
  const { to, label, icon: Icon } = item
  const isActive = isNavEntryActive(location.pathname, item)

  return (
    <LinkContainer
      component={RouterLink}
      to={to}
      isActive={isActive}
      isChild={isChild}
      title={label}
      aria-current={isActive ? "page" : undefined}
    >
      <Icon sx={{ color: "white", fontSize: 20 }} />
      <LinkTypography>{label}</LinkTypography>
    </LinkContainer>
  )
}

function AdminNavGroup({ group, open, onToggle }) {
  const location = useLocation()
  const groupActive = isNavGroupActive(location.pathname, group)
  const GroupIcon = group.icon
  const listId = `admin-nav-group-${group.id}`

  return (
    <Column sx={{ width: "100%", gap: 0.5 }}>
      <LinkContainer
        component="button"
        type="button"
        isActive={groupActive && !open}
        onClick={() => onToggle(group.id)}
        aria-expanded={open}
        aria-controls={listId}
        sx={{ border: "none" }}
      >
        <GroupIcon sx={{ color: "white", fontSize: 20 }} />
        <LinkTypography sx={{ flex: 1, textAlign: "left" }}>
          {group.label}
        </LinkTypography>
        <ExpandMoreIcon
          sx={{
            color: "white",
            fontSize: 20,
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s ease",
          }}
        />
      </LinkContainer>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <LinkGroupColumn id={listId} sx={{ pl: 0.5 }}>
          {group.items.map((item) => (
            <AdminNavLink key={item.to} item={item} isChild />
          ))}
        </LinkGroupColumn>
      </Collapse>
    </Column>
  )
}

/*
 * Open/closed state per group, remembered per browser. The group holding the
 * current page is always opened on navigation (and that is remembered too).
 */
function useOpenNavGroups() {
  const location = useLocation()
  const activeGroupId = findActiveNavGroupId(location.pathname)
  const [openGroups, setOpenGroups] = useState(() => {
    const initial = { ...defaultOpenGroups(), ...readStoredOpenGroups() }
    return activeGroupId ? { ...initial, [activeGroupId]: true } : initial
  })

  useEffect(() => {
    if (activeGroupId) {
      setOpenGroups((current) =>
        current[activeGroupId] ? current : { ...current, [activeGroupId]: true }
      )
    }
  }, [activeGroupId])

  useEffect(() => {
    writeStoredOpenGroups(openGroups)
  }, [openGroups])

  function toggleGroup(groupId) {
    setOpenGroups((current) => ({ ...current, [groupId]: !current[groupId] }))
  }

  return [openGroups, toggleGroup]
}

function AdminProfileAvatar({ profile }) {
  const initial =
    profile?.name?.[0] || profile?.email?.[0]?.toUpperCase() || "?"

  return (
    <Avatar
      sx={{
        width: 30,
        height: 30,
        fontSize: 12,
        bgcolor: alpha("#fff", 0.18),
        color: "white",
        border: "1px solid rgba(255, 255, 255, 0.25)",
      }}
    >
      {initial}
    </Avatar>
  )
}

function AdminBrandHeader() {
  return (
    <Link
      component={RouterLink}
      to="/admin"
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        textDecoration: "none",
      }}
    >
      <img
        src={NavLogo}
        style={{ borderRadius: "8px", width: "44px", display: "block" }}
        alt="OneBase square logo"
      />
      <Row sx={{ alignItems: "baseline", gap: 0.75 }}>
        <Typography sx={{ ...brandWordSx, fontSize: "1.25rem" }}>
          OneBase
        </Typography>
        <Typography sx={{ ...brandWordSx, fontSize: "1rem", opacity: 0.9 }}>
          Admin
        </Typography>
      </Row>
    </Link>
  )
}

export default function AdminSidebar() {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const profile = useSelector(selectAdminProfile)
  const [openGroups, toggleGroup] = useOpenNavGroups()

  function handleLogOut() {
    dispatch(adminLogout()).then(() => navigate("/admin/log-in/"))
  }

  return (
    <LinksColumn>
      <AdminBrandHeader />

      <Column
        sx={{
          mt: 4,
          justifyContent: "space-between",
          width: "100%",
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
        }}
      >
        <LinkGroupColumn component="nav" aria-label="Admin">
          <AdminNavLink item={DASHBOARD_NAV} />
          {navGroupsForFeatures(ADMIN_NAV_GROUPS, profile?.features).map(
            (group) => (
              <AdminNavGroup
                key={group.id}
                group={group}
                open={Boolean(openGroups[group.id])}
                onToggle={toggleGroup}
              />
            )
          )}
        </LinkGroupColumn>

        <Column sx={{ gap: 2, width: "100%", mt: "auto", pt: 2 }}>
          <LinkGroupColumn>
            {ADMIN_NAV_FOOTER.map((item) => (
              <AdminNavLink key={item.to} item={item} />
            ))}
          </LinkGroupColumn>
          <Divider sx={{ borderColor: "#F5EEFE", opacity: 0.1 }} />
          <LinkGroupColumn>
            <LinkContainer
              component="button"
              type="button"
              isActive={false}
              onClick={handleLogOut}
            >
              <LogoutOutlinedIcon sx={{ color: "white" }} />
              <LinkTypography>Log out</LinkTypography>
            </LinkContainer>
          </LinkGroupColumn>
          <AvatarLink
            component="div"
            isActive={false}
            sx={{ cursor: "default" }}
          >
            <AdminProfileAvatar profile={profile} />
            <Row
              sx={{
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
                minWidth: 0,
              }}
            >
              <Column sx={{ minWidth: 0, flex: 1 }}>
                <LinkTypography sx={{ textWrap: "wrap" }}>
                  {profile?.name || "Staff user"}
                </LinkTypography>
                {profile?.email ? (
                  <SubtleLinkTypography>{profile.email}</SubtleLinkTypography>
                ) : null}
              </Column>
              <KeyboardArrowRightIcon sx={{ fill: "white", flexShrink: 0 }} />
            </Row>
          </AvatarLink>
        </Column>
      </Column>
    </LinksColumn>
  )
}

export const DRAWER_WIDTH = 268
